import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { HeartIcon, ShieldCheckIcon, BookOpenIcon, SmileIcon, CheckCircleIcon } from 'lucide-react';
export function ColostomyCarePage() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: benefitsRef,
    isVisible: isBenefitsVisible
  } = useScrollAnimation();
  const {
    ref: detailsRef,
    isVisible: isDetailsVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  return <main data-id="element-494">
      <PageBanner title="Colostomy & Ostomy Care" subtitle="Comprehensive support and education for patients with bowel diversions." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'Colostomy Care',
      path: '/services/colostomy-care'
    }]} data-id="element-495" />

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-light-alt to-light" data-id="element-496">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-497">
          <div ref={introRef} className={`transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-498">
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-499">
              <HeartIcon className="h-4 w-4 text-primary" data-id="element-500" />
              <span className="text-primary text-sm font-semibold" data-id="element-501">
                Compassionate Support
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 leading-tight" data-id="element-502">
              Empowering Patients Through Expert Care
            </h2>
            <div className="space-y-5 text-dark-muted text-lg leading-relaxed" data-id="element-503">
              <p data-id="element-504">
                Our team provides comprehensive support for patients with bowel
                diversions. We focus on skin integrity, appliance management,
                and empowering patients through education on lifestyle
                adjustments and self-care.
              </p>
            </div>

            {/* RN Badge */}
            <div className="mt-8 flex items-center bg-primary/5 border border-primary/10 rounded-xl p-5" data-id="element-505">
              <ShieldCheckIcon className="h-10 w-10 text-primary mr-4 flex-shrink-0" data-id="element-506" />
              <div data-id="element-507">
                <h4 className="font-bold text-dark" data-id="element-508">RN Supervised Care</h4>
                <p className="text-sm text-dark-muted" data-id="element-509">
                  All ostomy care plans are developed and supervised by
                  experienced Registered Nurses.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-gradient-to-tr from-light to-light-alt" data-id="element-510">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-511">
          <div ref={benefitsRef} className={`transition-all duration-1000 ease-out ${isBenefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-512">
            <div className="text-center mb-14" data-id="element-513">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-514">
                Comprehensive Ostomy Support
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-515"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-516">
              {[{
              icon: ShieldCheckIcon,
              title: 'Skin Integrity',
              desc: 'Proactive management to prevent irritation and maintain healthy peristomal skin.'
            }, {
              icon: HeartIcon,
              title: 'Appliance Management',
              desc: 'Expert assistance with pouching systems to ensure a secure and comfortable fit.'
            }, {
              icon: BookOpenIcon,
              title: 'Patient Education',
              desc: 'Teaching self-care techniques to build confidence and independence.'
            }, {
              icon: SmileIcon,
              title: 'Lifestyle Support',
              desc: 'Guidance on diet, clothing, and activities to help you live life to the fullest.'
            }].map((benefit, idx) => <div key={idx} className="bg-light-alt rounded-2xl p-7 shadow-card hover:shadow-card-hover border border-gray-200 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-517">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300" data-id="element-518">
                    <benefit.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" data-id="element-519" />
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-3" data-id="element-520">
                    {benefit.title}
                  </h3>
                  <p className="text-dark-muted text-sm leading-relaxed" data-id="element-521">
                    {benefit.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-light-alt to-light" data-id="element-522">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-523">
          <div ref={detailsRef} className={`transition-all duration-1000 ease-out ${isDetailsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-524">
            <h2 className="text-3xl font-bold text-dark mb-6" data-id="element-525">
              Our Ostomy Care Services Include
            </h2>
            <p className="text-dark-muted text-lg leading-relaxed mb-8" data-id="element-526">
              Adjusting to life with an ostomy can be challenging. Our
              compassionate team is here to provide the physical care and
              emotional support you need to adapt successfully.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-527">
              {['Ostomy appliance changes & management', 'Peristomal skin assessment', 'Patient & family education', 'Lifestyle adjustment guidance', 'Output monitoring', 'Self-care skill building'].map((item, idx) => <div key={idx} className="flex items-center p-4 bg-light rounded-xl border border-gray-200" data-id="element-528">
                  <CheckCircleIcon className="h-6 w-6 text-secondary mr-3 flex-shrink-0" data-id="element-529" />
                  <span className="text-dark font-medium" data-id="element-530">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-light" data-id="element-531">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-532">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-533">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-534"></div>
            <div className="relative z-10" data-id="element-535">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-536">
                Need Support with Ostomy Care?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-537">
                Contact us today to discuss your care needs and schedule a
                consultation with our nursing team.
              </p>
              <a href="#/contact" className="inline-block bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-538">
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-539" />
    </main>;
}